import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import api, { formatCurrency, formatPercent } from "../lib/api";
import { deriveHeroData, CONFIDENCE_DISCLOSURE } from "../lib/heroBalance";
import PartnerBalanceDisclosure from "./PartnerBalanceDisclosure";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { TrendingUp, TrendingDown, Sparkles, Wallet, Activity, Trophy, AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Animated count-up hook (no extra deps).
 * Eases from 0 → target over `durationMs` using requestAnimationFrame.
 */
const useCountUp = (target, durationMs = 1400) => {
  const [value, setValue] = useState(0);
  const frame = useRef();

  useEffect(() => {
    if (target === null || target === undefined || isNaN(target)) return;
    const start = performance.now();
    const startValue = 0;

    const step = (now) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / durationMs, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(startValue + (target - startValue) * eased);
      if (t < 1) frame.current = requestAnimationFrame(step);
    };

    frame.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame.current);
  }, [target, durationMs]);

  return value;
};

/**
 * Hero metric card shown at the top of Client/Admin dashboards.
 * - Animated count-up on the combined AUM
 * - Emerald glow pulse when net P&L is positive ("scaling up")
 * - Provider breakdown chips (Native only)
 *
 * Monaxa is intentionally omitted from this card while every seeded
 * account is `is_demo: True` — see FORGE-20260522-001. Demo balances are
 * surfaced (and clearly labelled as such) on the dedicated /partner-account
 * page only. When real data sources land and write `is_demo: false`, the
 * provider can be re-added to this hero. (PocketOption surfaces were
 * removed entirely — STARSEED-CAPMARKETS-080.)
 *
 * @param {"me"|"platform"} scope    which analytics endpoint to hit
 * @param {string}          label    headline label (e.g. "My Portfolio")
 * @param {string}          caption  subtitle text
 */
const PortfolioGrowthHero = ({ scope = "me", label = "My Portfolio", caption }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // `unavailable` is a THIRD state, distinct from both "loading" and a real
  // zero balance. Before STARSEED-CAPMARKETS-094 a failed fetch fell back to
  // `combined_balance: 0`, which rendered an animated, confident $0 — a wrong
  // number about a member's money, presented as fact. Never again: if we
  // cannot state the position, we say so.
  const [unavailable, setUnavailable] = useState(false);
  const [milestone, setMilestone] = useState(null);
  const celebrated = useRef(false);

  const fireCelebration = useCallback((tierLabel) => {
    if (celebrated.current) return;
    celebrated.current = true;
    const duration = 2500;
    const end = Date.now() + duration;
    const colors = ["#10B981", "#0055FF", "#F59E0B", "#34D399"];
    (function frame() {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 70,
        origin: { x: 0, y: 0.7 },
        colors,
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 70,
        origin: { x: 1, y: 0.7 },
        colors,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
    toast.success(`You've reached ${tierLabel} tier!`, {
      description: "Your capital band just leveled up. Celebrating with you.",
      duration: 6000,
    });
  }, []);

  /**
   * Load the hero figures.
   *
   * Both failure paths — a rejected request and a well-formed response whose
   * payload cannot support a trustworthy number — resolve to `unavailable`,
   * NOT to zero. `deriveHeroData` owns the second judgement; see
   * ../lib/heroBalance.js.
   *
   * Monaxa is intentionally omitted from this card (FORGE-20260522-001);
   * both endpoints contribute the Native Fund block only.
   */
  // STARSEED-CAPMARKETS-218 — the ledger-derived estimate, on the page a
  // member actually lands on. THREE distinct values, because two of them
  // were collapsed in the first version and every member paid for it:
  //   undefined  in flight                -> "Calculating…" (hero-ledger-loading)
  //   null       the fetch failed         -> "could not calculate"
  //   object     answered                 -> its own four states
  // Starting at `null` meant the could-not-calculate copy showed for the
  // whole duration of a multi-second partner call, on every dashboard load,
  // while nothing had failed. (`/code-review`, PR #2488.)
  const [ledgerBal, setLedgerBal] = useState(undefined);
  // Guards a superseded response: `load` re-runs on scope change, and the
  // fetch is fire-and-forget. Same token idiom as the sibling page's
  // `detailReqRef`, rather than inventing a second convention.
  const ledgerReqRef = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    try {
      const path = scope === "platform" ? "/analytics/summary" : "/analytics/me";
      const r = await api.get(path);

      // Fetched SEPARATELY and never awaited into the hero's critical path.
      // The partner ledger call takes seconds (it walks the whole book), and
      // folding it into /analytics/me would make every member's landing page
      // wait on the partner. The dashboard renders immediately; the estimate
      // fills in. A failure leaves `ledgerBal` null, which renders as
      // "could not calculate" — never as a balance.
      if (scope !== "platform") {
        const token = ++ledgerReqRef.current;
        // Back to in-flight BEFORE the request. Without this, a retry after a
        // failure leaves `ledgerBal` at `null` — the could-not-calculate
        // branch — so the member reads "unavailable" for the whole duration
        // of the retry, while it is loading. That is the exact defect fixed
        // one round ago on the INITIAL load, surviving in the retry path
        // because the fix addressed the initial value and not the
        // transition. (`/code-review`, PR #2488.)
        setLedgerBal(undefined);
        api.get("/monaxa/balance")
          .then((b) => { if (token === ledgerReqRef.current) setLedgerBal(b.data); })
          .catch(() => { if (token === ledgerReqRef.current) setLedgerBal(null); });
      }
      const model = deriveHeroData(scope, r.data);
      if (!model) {
        setUnavailable(true);
        return;
      }
      setData(model);

      if (scope !== "platform") {
        // Milestone check (personal only) — fires confetti + toast on upgrade.
        // Non-critical: a failure here must never take down the balance.
        try {
          const ms = await api.post("/milestones/me/check");
          setMilestone(ms.data);
          if (ms.data.upgraded && ms.data.current_tier?.label) {
            // small delay so hero can mount before confetti
            setTimeout(() => fireCelebration(ms.data.current_tier.label), 600);
          }
        } catch { /* silent — non-critical */ }
      }
    } catch {
      setUnavailable(true);
    } finally {
      setLoading(false);
    }
  }, [scope, fireCelebration]);

  useEffect(() => { load(); }, [load]);

  /* CAPMARKETS-238 — four states, not two.
   *
   * This was `(combined_pnl || 0) >= 0`, which makes "no gain" indistinguishable
   * from "a gain". CAPMARKETS-235 fixed the two places that PRINTED a number
   * from it — the percentage and the SCALING UP badge — and left six that PAINT
   * with it: the card glow, the pulse animation, the radial gradient, the
   * Sparkles icon, and the orb's background and arrow. So Jamie's dashboard
   * still carried a green pulsing up-arrow beside a figure whose growth we
   * cannot compute.
   *
   * `unknown` is the state that did not exist. A member whose capital is all
   * partner-held has `pnl_scope === "native_only"`, so no growth signal covers
   * the displayed total at all (CAPMARKETS-190 — there is no trustworthy
   * partner-side P&L). That is not "flat", and it is certainly not "up".
   */
  const pnlCoversTotal = data?.pnl_scope === "all";
  const growth = !pnlCoversTotal
    ? "unknown"
    : (data?.combined_pnl || 0) > 0
      ? "up"
      : (data?.combined_pnl || 0) < 0
        ? "down"
        : "flat";
  // Retained for the one place that only needs the sign of a figure already
  // guarded by `pnl_scope === "all"`.
  const isPositive = growth === "up";
  const animatedBalance = useCountUp(data?.combined_balance || 0);
  const animatedPnl = useCountUp(data?.combined_pnl || 0);

  const skeleton = (
    <Card className="bg-white border-slate-200 mb-6">
      <CardContent className="p-8">
        <div className="animate-pulse space-y-3">
          <div className="h-3 bg-slate-100 rounded w-32"></div>
          <div className="h-10 bg-slate-100 rounded w-64"></div>
          <div className="h-3 bg-slate-100 rounded w-48"></div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) return skeleton;

  // Explicit "we don't know" state. This branch MUST come before any render
  // that would substitute a zero — showing $0 when the figure failed to load
  // states something false about the member's money.
  if (unavailable) {
    return (
      <Card
        className="bg-white border-amber-200 mb-6"
        data-testid="portfolio-hero-unavailable"
      >
        <CardContent className="p-8">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="h-10 w-10 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
              <AlertTriangle className="text-amber-600" size={20} />
            </div>
            <div className="flex-1 min-w-[240px]">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">
                {label}
              </p>
              <p className="text-lg font-semibold text-slate-900">
                Balance temporarily unavailable
              </p>
              <p className="text-sm text-slate-500 mt-1">
                We couldn&apos;t load these figures just now. Rather than show a
                number we can&apos;t stand behind, we&apos;re holding off —
                please try again in a moment.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              className="shrink-0"
              data-testid="portfolio-hero-retry"
            >
              <RefreshCw size={14} className="mr-2" /> Try again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return skeleton;

  // CAPMARKETS-238. Every visual growth signal keys off `growth`, so "unknown"
  // and "flat" render neutral instead of borrowing the winning treatment.
  const glowClass = growth === "up"
    ? "border-emerald-200 shadow-[0_8px_32px_-4px_rgba(16,185,129,0.22)] bg-gradient-to-br from-white via-white to-emerald-50/60"
    : "border-slate-200 bg-white";
  const pulseRing = growth === "up" ? "animate-pulse-emerald" : "";
  const pnlColor = growth === "up"
    ? "text-emerald-600"
    : growth === "down"
      ? "text-red-500"
      : "text-slate-500";
  // A directional arrow is a claim about direction. With no growth signal,
  // don't make one — `Activity` is the non-directional glyph.
  const TrendIcon = growth === "up"
    ? TrendingUp
    : growth === "down"
      ? TrendingDown
      : Activity;

  return (
    <Card
      data-testid="portfolio-growth-hero"
      className={`relative overflow-hidden mb-6 transition-all duration-500 ${glowClass}`}
    >
      {/* Decorative radial accent */}
      <div
        className="pointer-events-none absolute -right-32 -top-32 h-72 w-72 rounded-full opacity-30 blur-3xl"
        style={{
          background: growth === "up"
            ? "radial-gradient(circle, rgba(16,185,129,0.45), transparent 70%)"
            : "radial-gradient(circle, rgba(0,85,255,0.18), transparent 70%)",
        }}
      />
      <CardContent className="relative p-8">
        <div className="flex items-start justify-between flex-wrap gap-6">
          {/* Left: combined balance */}
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Sparkles className={growth === "up" ? "text-emerald-500" : "text-blue-600"} size={14} />
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                {label}
              </span>
              {/* CAPMARKETS-235, rekeyed by 238. The guard was `pnl >= 0`, so a
                  gain of exactly zero earned the badge — and a member whose
                  capital is all partner-held has a native P&L of exactly zero.
                  The result was "SCALING UP" beside "$0.00" on a six-figure
                  position.

                  `growth === "up"` carries both conditions 235 added: the P&L
                  must cover the whole displayed total, and it must be an actual
                  gain. Expressed once now, because 238 found six other places
                  painting from the same two-state boolean. */}
              {growth === "up" && (
                <Badge
                  className="bg-emerald-50 text-emerald-700 border-emerald-200 ml-1"
                  data-testid="scaling-badge"
                >
                  SCALING UP
                </Badge>
              )}
              {milestone?.current_tier?.key && milestone.current_tier.key !== "none" && (
                <Badge
                  className="bg-gradient-to-r from-blue-600 to-emerald-500 text-white border-0"
                  data-testid="tier-badge"
                >
                  <Trophy size={12} className="mr-1" />
                  {milestone.current_tier.label.toUpperCase()}
                </Badge>
              )}
            </div>

            <div className="flex items-baseline gap-3 flex-wrap">
              <span
                className="text-5xl font-semibold tracking-tight text-slate-900 tabular-nums"
                data-testid="hero-balance"
              >
                {formatCurrency(animatedBalance)}
              </span>
              {/* CAPMARKETS-235. `combined_pnl` and `return_pct` describe the
                  NATIVE fund alone; there is no trustworthy partner-side P&L
                  (190 — the partner reports 0.00 on pooled accounts and the
                  realised figure lives in a PAMM system we cannot read).

                  Rendered beside a COMBINED balance, "+$0.00 (+0.00%)" states a
                  return on money that return was never computed over — and for
                  a member whose capital is entirely partner-held it reads as
                  "you have made nothing" on a six-figure position. Suppressed
                  rather than relabelled: a growth figure covering part of the
                  total is a footnote at best, and this is the largest type on
                  the page. */}
              {data.pnl_scope === "all" && (
                <span className={`inline-flex items-center gap-1 text-base font-semibold ${pnlColor} tabular-nums`} data-testid="hero-pnl">
                  <TrendIcon size={16} />
                  {isPositive ? "+" : ""}{formatCurrency(animatedPnl)}
                  <span className="text-slate-400 font-normal ml-1">
                    ({formatPercent(data.return_pct)})
                  </span>
                </span>
              )}
            </div>

            <p className="text-sm text-slate-500 mt-3">
              {/* CAPMARKETS-237. The combined caption is checked BEFORE
                  `caption`, so a caller cannot override it.

                  It used to read `{caption || (…)}`, and ClientDashboard passed
                  a hard-coded "Your balance from the Native Fund" — which won,
                  and sat under a $335,277.82 total of which the Native Fund was
                  $0.00. Removing that one prop fixes today's page; this ordering
                  is what stops the next caller reintroducing it, because no
                  caption a caller writes can know whether the figure it labels
                  is combined. Only the derivation knows that. */}
              {data.is_combined
                ? "Your total position — the Native Fund plus capital held by our partner."
                : caption || (scope === "platform"
                  ? `Combined AUM across ${data.qualified_users || 0} qualified users — native fund only.`
                  : "Your balance from the Native Fund. Glows when you're in the green.")}
            </p>
            {/* CAPMARKETS-235. Hidden when the displayed total is combined.
                `/milestones/me/check` computes tiers from the NATIVE balance
                alone, so for a partner-funded member it reported "Next: Pillar,
                $0.00 / $250,000.00" — zero progress for someone already past the
                threshold. Feeding the combined figure into a bar whose TIER was
                chosen on a different basis would swap that for a bar reading
                100% while still captioned "Next", which is not better.

                Hidden rather than guessed at, until the milestone service is
                partner-aware. Tracked on the ticket. */}
            {scope !== "platform" && milestone?.next_tier && !data.is_combined && (
              <div className="mt-3" data-testid="next-tier-progress">
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>
                    Next: <span className="font-semibold text-slate-700">{milestone.next_tier.label}</span>
                  </span>
                  <span>
                    {formatCurrency(data.combined_balance)} / {formatCurrency(milestone.next_tier.threshold)}
                  </span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-600 to-emerald-500 rounded-full transition-all duration-700"
                    style={{
                      width: `${Math.min(100, (data.combined_balance / milestone.next_tier.threshold) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Right: pulsing orb */}
          <div className="flex items-center justify-center">
            <div
              className={`relative flex items-center justify-center h-20 w-20 rounded-full ${
                growth === "up" ? "bg-emerald-50" : "bg-blue-50"
              } ${pulseRing}`}
            >
              <TrendIcon className={growth === "up" ? "text-emerald-600" : "text-blue-600"} size={32} />
            </div>
          </div>
        </div>

        {/* Provider breakdown chips. Monaxa is intentionally omitted while
            every seeded account is is_demo (see FORGE-20260522-001). Demo
            balances are still discoverable on /partner-account. */}
        <div className="grid grid-cols-1 gap-3 mt-6">
          <ProviderChip
            icon={Wallet}
            label="Native Fund"
            value={formatCurrency(data.native_balance)}
            accent="blue"
          />
        </div>

        {/* STARSEED-CAPMARKETS-218 — the ledger-derived estimate, beside the
            transcribed figure rather than replacing it. Both are shown because
            they answer different questions: the transcribed one is what a human
            read off a statement and is now FROZEN (screenshots stopped
            2026-08-28); this one tracks the partner's movements and is an
            estimate. Collapsing them into one number would hide which is which.
            Four states, kept apart — CAPMARKETS-181.

            ORDER IS LOAD-BEARING (CAPMARKETS-219): this renders ABOVE the
            transcribed figure. When the frozen statement card sat on top,
            members read it as the live number and the estimate as noise —
            Jamie Fischer reported "still seeing August 26" while both
            cards were on their screen. The moving figure leads; the frozen
            one follows as history. Do not reorder without replacing the
            frozen card's framing. */}
        {scope !== "platform" && (
          <div className="mt-3 rounded-lg border border-platinum/10 p-4" data-testid="hero-ledger-balance">
            {ledgerBal === undefined ? (
              // In flight. Deliberately silent — claiming a failure here is
              // what the first version did.
              <p className="text-sm text-muted-foreground" data-testid="hero-ledger-loading">
                Calculating from your transaction history…
              </p>
            ) : ledgerBal === null ? (
              <p className="text-sm text-muted-foreground" data-testid="hero-ledger-unavailable">
                Calculated balance unavailable right now. This is not a balance of zero.
              </p>
            ) : ledgerBal.status === "no_accounts_mapped" ? (
              <p className="text-sm text-muted-foreground" data-testid="hero-ledger-unmapped">
                No partner account is connected to your profile yet.
              </p>
            ) : ledgerBal.total === null || ledgerBal.total === undefined ? (
              <p className="text-sm text-muted-foreground" data-testid="hero-ledger-no-movements">
                We have no usable movements on your accounts yet, so there is no
                calculated figure to show. That is not a balance of zero.
              </p>
            ) : (
              <>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Calculated from your transaction history
                </p>
                <p className="text-2xl font-semibold text-gold" data-testid="hero-ledger-total">
                  {ledgerBal.total.toLocaleString(undefined, {
                    style: "currency", currency: "USD",
                  })}
                </p>
                <p className="text-sm text-muted-foreground" data-testid="hero-ledger-disclosure">
                  <strong>Estimate.</strong>{" "}
                  {CONFIDENCE_DISCLOSURE.ledger_derived.detail}
                </p>
                {ledgerBal.includes_unexplained_corrections && (
                  <p className="text-xs text-muted-foreground" data-testid="hero-ledger-corrections">
                    Includes partner adjustments still being reconciled, so it may change.
                  </p>
                )}
                {ledgerBal.as_of && (
                  <p className="text-xs text-muted-foreground" data-testid="hero-ledger-asof">
                    Calculated {new Date(ledgerBal.as_of).toLocaleString()}
                  </p>
                )}
              </>
            )}
          </div>
        )}
        {/* Partner-held capital, disclosed separately rather than summed into
            the headline. The two have different custodians and independent
            freshness, and a combined figure that cannot say who holds what is
            not auditable (CAPMARKETS-115 AC4). */}
        <PartnerBalanceDisclosure partner={data.partner} />
      </CardContent>
    </Card>
  );
};

const ProviderChip = ({ icon: Icon, label, value, accent = "slate" }) => {
  const accentCls =
    accent === "blue"
      ? "text-blue-600 bg-blue-50"
      : accent === "emerald"
      ? "text-emerald-600 bg-emerald-50"
      : "text-slate-600 bg-slate-100";

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white/70"
      data-testid={`provider-chip-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className={`h-9 w-9 rounded-md flex items-center justify-center ${accentCls}`}>
        <Icon size={16} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
        <p className="text-sm font-semibold text-slate-900 tabular-nums truncate">{value}</p>
      </div>
    </div>
  );
};

export default PortfolioGrowthHero;
